<template>
  <section id="ai-development" class="section aidev on-dark" aria-labelledby="aidev-title">
    <div class="aidev__grid-tex" aria-hidden="true"></div>

    <div class="container aidev__inner">
      <div class="aidev__head">
        <div class="section-head" v-reveal>
          <span class="eyebrow eyebrow--light">03 / How We Build with AI</span>
          <h2 id="aidev-title" class="section-title section-title--light">
            <span class="en">How We Build with AI</span>
            速さと品質を、両立させる開発のしかた
          </h2>
        </div>
        <p class="aidev__catch" v-reveal="80">AIで速く。人とテストで、確かに。</p>
        <p class="aidev__intro" v-reveal="140">
          私たちは、開発プロセスにAIを積極的に取り入れています。ただし、AIに任せる部分と、人が担保する部分を明確に分けています。
        </p>
      </div>

      <!-- Build pipeline -->
      <div class="pipeline" v-reveal="120" role="img"
           aria-label="開発フロー: AI生成 から 人がレビュー、自動テストを経て リリースへ">
        <span class="pipeline__phase pipeline__phase--speed">速さ = AI</span>
        <span class="pipeline__phase pipeline__phase--sure">確かさ = 人 + テスト</span>

        <div class="pipeline__track">
          <div class="pnode pnode--accent">
            <span class="pnode__ring">
              <svg viewBox="0 0 32 32" width="26" height="26" aria-hidden="true">
                <path d="M16 5l2.2 6.3L24.5 13l-6.3 2.1L16 21l-2.2-5.9L7.5 13l6.3-1.7z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round" />
              </svg>
            </span>
            <span class="pnode__step">01</span>
            <span class="pnode__label">AI生成</span>
            <span class="pnode__sub">AIコーディング支援</span>
          </div>

          <div class="pconn"><span class="pconn__pulse" style="--d: 0s"></span></div>

          <div class="pnode">
            <span class="pnode__ring">
              <svg viewBox="0 0 32 32" width="26" height="26" aria-hidden="true">
                <circle cx="16" cy="12" r="4.4" fill="none" stroke="currentColor" stroke-width="1.7" />
                <path d="M7 26c1.6-4.2 4.9-6.4 9-6.4s7.4 2.2 9 6.4" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
                <path d="M20.5 12.5l1.6 1.6 3-3.4" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </span>
            <span class="pnode__step">02</span>
            <span class="pnode__label">人がレビュー</span>
            <span class="pnode__sub">PRレビュー</span>
          </div>

          <div class="pconn"><span class="pconn__pulse" style="--d: 0.5s"></span></div>

          <div class="pnode">
            <span class="pnode__ring">
              <svg viewBox="0 0 32 32" width="26" height="26" aria-hidden="true">
                <path d="M9 5h14M11 5v7l-4.6 8.4A2.2 2.2 0 0 0 8.4 24h15.2a2.2 2.2 0 0 0 2-3.6L21 12V5" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round" />
                <path d="M12 18.5l2.2 2.2 4.2-4.4" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </span>
            <span class="pnode__step">03</span>
            <span class="pnode__label">自動テスト</span>
            <span class="pnode__sub">自動テストで検証</span>
          </div>

          <div class="pconn"><span class="pconn__pulse" style="--d: 1s"></span></div>

          <div class="pnode pnode--accent">
            <span class="pnode__ring">
              <svg viewBox="0 0 32 32" width="26" height="26" aria-hidden="true">
                <path d="M16 4c4.5 2 6.8 6.2 6.8 11.4L16 22l-6.8-6.6C9.2 10.2 11.5 6 16 4z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round" />
                <circle cx="16" cy="13" r="2.4" fill="none" stroke="currentColor" stroke-width="1.7" />
                <path d="M12 23l-2 4M20 23l2 4M16 24v4" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
              </svg>
            </span>
            <span class="pnode__step">04</span>
            <span class="pnode__label">リリース</span>
            <span class="pnode__sub">安定稼働まで</span>
          </div>
        </div>
      </div>

      <!-- Two responsibilities -->
      <div class="split">
        <div class="split__col split__col--speed" v-reveal="80">
          <span class="split__tag">SPEED</span>
          <h3 class="split__title">速さは、AIに。</h3>
          <p class="split__body">
            Claude Code をはじめとするAIコーディング支援ツールを活用し、実装のたたき台づくりや定型的なコードの生成を高速化します。これにより、お客様のアイデアを早い段階で「動くかたち」にしてお見せできます。
          </p>
        </div>

        <div class="split__col split__col--sure" v-reveal="180">
          <span class="split__tag split__tag--sure">CERTAINTY</span>
          <h3 class="split__title">確かさは、人とテストに。</h3>
          <p class="split__body">AIが生成したコードは、そのまま採用しません。</p>
          <ul class="assure">
            <li>
              <span class="assure__mark" aria-hidden="true">
                <svg viewBox="0 0 20 20" width="18" height="18"><path d="M4 10.5l3.5 3.5L16 5.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /></svg>
              </span>
              <p><strong>必ず人間がレビューします。</strong> すべての変更は、経験のあるエンジニアがコードレビュー（PRレビュー）で確認してから取り込みます。</p>
            </li>
            <li>
              <span class="assure__mark" aria-hidden="true">
                <svg viewBox="0 0 20 20" width="18" height="18"><path d="M4 10.5l3.5 3.5L16 5.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /></svg>
              </span>
              <p><strong>自動テストで二重に守ります。</strong> 主要な処理には自動テストを用意し、AIが書いたコードも人が直したコードも、同じ基準で検証されます。</p>
            </li>
          </ul>
        </div>
      </div>

      <p class="aidev__closing" v-reveal="80">
        「AIが書いたから速いが品質が不安」——そんな心配をなくすために、私たちは速さと品質の役割分担を仕組みにしています。
      </p>
    </div>
  </section>
</template>

<script>
export default {
  name: 'AiDevelopmentSection',
}
</script>

<style scoped lang="scss">
.aidev {
  position: relative;
  background: var(--dark);
  color: var(--on-dark);
  overflow: hidden;
}

.aidev__grid-tex {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(var(--dark-line-soft) 1px, transparent 1px),
    linear-gradient(90deg, var(--dark-line-soft) 1px, transparent 1px);
  background-size: 44px 44px;
  mask-image: radial-gradient(ellipse 80% 70% at 50% 30%, #000 40%, transparent 100%);
  -webkit-mask-image: radial-gradient(ellipse 80% 70% at 50% 30%, #000 40%, transparent 100%);
  pointer-events: none;
}

.aidev__inner {
  position: relative;
  z-index: 1;
}

.eyebrow--light {
  color: var(--accent-bright);
}

.section-title--light {
  color: var(--on-dark);
}

.section-title--light .en {
  color: var(--on-dark-muted);
}

.aidev__catch {
  margin-top: 28px;
  font-family: var(--font-jp);
  font-weight: 700;
  font-size: clamp(1.5rem, 3.4vw, 2.3rem);
  line-height: 1.4;
  color: #fff;
}

.aidev__intro {
  max-width: 54ch;
  margin-top: 18px;
  font-size: clamp(1rem, 1.3vw, 1.1rem);
  line-height: 1.9;
  color: var(--on-dark-soft);
}

/* --- Pipeline --- */
.pipeline {
  position: relative;
  margin-top: clamp(52px, 7vw, 88px);
  padding: clamp(40px, 4vw, 56px) clamp(20px, 3vw, 40px) clamp(32px, 4vw, 44px);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.04), rgba(255, 255, 255, 0.01));
  border: 1px solid var(--dark-line);
  border-radius: var(--radius-lg);
}

.pipeline__phase {
  position: absolute;
  top: -11px;
  font-family: var(--font-mono);
  font-size: 0.66rem;
  letter-spacing: 0.14em;
  padding: 3px 12px;
  background: var(--dark);
  border: 1px solid var(--dark-line);
  border-radius: 999px;
  white-space: nowrap;
}

.pipeline__phase--speed {
  left: clamp(20px, 6vw, 60px);
  color: var(--accent-bright);
  border-color: var(--accent-line);
}

.pipeline__phase--sure {
  right: clamp(20px, 6vw, 60px);
  color: var(--on-dark-soft);
}

.pipeline__track {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.pnode {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  flex: 0 0 auto;
  width: 130px;
}

.pnode__ring {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 62px;
  height: 62px;
  border-radius: 50%;
  border: 1px solid var(--dark-line);
  background: var(--dark-raised);
  color: var(--on-dark-soft);
  margin-bottom: 16px;
  transition: transform 0.4s var(--ease);
}

.pnode--accent .pnode__ring {
  color: var(--dark);
  border-color: transparent;
  background: var(--accent-bright);
  box-shadow: 0 12px 30px -12px rgba(133, 114, 67, 0.55);
}

.pnode__step {
  font-family: var(--font-mono);
  font-size: 0.66rem;
  letter-spacing: 0.1em;
  color: var(--on-dark-muted);
}

.pnode__label {
  margin-top: 6px;
  font-family: var(--font-jp);
  font-weight: 700;
  font-size: 1.02rem;
  color: #fff;
}

.pnode__sub {
  margin-top: 4px;
  font-size: 0.74rem;
  color: var(--on-dark-muted);
}

.pconn {
  position: relative;
  flex: 1 1 auto;
  height: 1px;
  margin-top: 31px;
  min-width: 20px;
  background: var(--dark-line);
}

.pconn__pulse {
  position: absolute;
  top: 50%;
  left: 0;
  width: 26px;
  height: 2px;
  margin-top: -1px;
  border-radius: 2px;
  background: linear-gradient(90deg, transparent, var(--accent-bright));
  animation: pulseMove 2.6s linear infinite;
  animation-delay: var(--d, 0s);
}

@keyframes pulseMove {
  0% {
    transform: translateX(-10px);
    opacity: 0;
  }
  15% {
    opacity: 1;
  }
  85% {
    opacity: 1;
  }
  100% {
    transform: translateX(calc(100% + 10px));
    opacity: 0;
  }
}

/* --- Split blocks --- */
.split {
  display: grid;
  grid-template-columns: 0.85fr 1.15fr;
  gap: 24px;
  margin-top: clamp(52px, 6vw, 80px);
}

.split__col {
  padding: clamp(28px, 3vw, 40px);
  border: 1px solid var(--dark-line);
  border-radius: var(--radius-lg);
  background: rgba(255, 255, 255, 0.02);
}

.split__col--sure {
  background: linear-gradient(180deg, rgba(133, 114, 67, 0.12), rgba(255, 255, 255, 0.02));
  border-color: rgba(133, 114, 67, 0.4);
}

.split__tag {
  font-family: var(--font-mono);
  font-size: 0.7rem;
  letter-spacing: 0.2em;
  color: var(--accent-bright);
}

.split__tag--sure {
  color: #fff;
}

.split__title {
  margin-top: 14px;
  font-family: var(--font-jp);
  font-weight: 700;
  font-size: clamp(1.3rem, 2.4vw, 1.7rem);
  color: #fff;
}

.split__body {
  margin-top: 16px;
  font-size: 0.98rem;
  line-height: 1.9;
  color: var(--on-dark-soft);
}

.assure {
  display: flex;
  flex-direction: column;
  gap: 18px;
  margin-top: 22px;
}

.assure li {
  display: flex;
  gap: 14px;
  align-items: flex-start;
}

.assure__mark {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: var(--accent-bright);
  color: var(--dark);
  margin-top: 2px;
}

.assure p {
  font-size: 0.95rem;
  line-height: 1.85;
  color: var(--on-dark-soft);
}

.assure strong {
  color: #fff;
  font-weight: 700;
}

.aidev__closing {
  max-width: 60ch;
  margin: clamp(44px, 5vw, 64px) auto 0;
  text-align: center;
  font-family: var(--font-jp);
  font-weight: 500;
  font-size: clamp(1.05rem, 1.7vw, 1.3rem);
  line-height: 1.8;
  color: #fff;
}

@media (max-width: 860px) {
  .split {
    grid-template-columns: 1fr;
  }
  /* Pipeline goes vertical on narrow screens */
  .pipeline__phase {
    display: none;
  }
  .pipeline__track {
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
  }
  .pnode {
    flex-direction: row;
    width: 100%;
    text-align: left;
    align-items: center;
    gap: 16px;
  }
  .pnode__ring {
    margin-bottom: 0;
  }
  .pnode__step {
    display: none;
  }
  .pnode__label {
    margin-top: 0;
  }
  .pconn {
    width: 1px;
    height: 26px;
    min-width: 0;
    margin: 6px 0 6px 30px;
    background: var(--dark-line);
  }
  .pconn__pulse {
    width: 2px;
    height: 22px;
    top: 0;
    left: 50%;
    margin-top: 0;
    margin-left: -1px;
    background: linear-gradient(180deg, transparent, var(--accent-bright));
    animation: pulseMoveV 2.6s linear infinite;
    animation-delay: var(--d, 0s);
  }
}

@keyframes pulseMoveV {
  0% {
    transform: translateY(-8px);
    opacity: 0;
  }
  15% {
    opacity: 1;
  }
  85% {
    opacity: 1;
  }
  100% {
    transform: translateY(calc(100% + 8px));
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .pconn__pulse {
    animation: none;
    opacity: 0.6;
  }
}
</style>
